/**
 * React hook for managing conversations via the backend API.
 */

import { useCallback, useEffect, useRef } from 'react'
import { useChatStore } from '@/stores/chatStore'
import type { Conversation, ConversationSummary } from '@/stores/chatStore'
import { apiClient } from '@/services/apiClient'
import type { ReplyCut } from '@/services/ragConnector'
import { useAuthStore } from '@/stores/authStore'

interface MessageCreate {
  role: 'user' | 'assistant' | 'system'
  content: string
  sources?: unknown[]
  error?: string
  cut?: ReplyCut
}

interface SavedAttachmentMessage {
  id: string
  media_url: string | null
  media_type: string | null
}

/** What became of a request to open a conversation (SA-974, SA-975).
 * `failed`: not opened and not known gone. That is a server or network
 * failure, or an answer dropped because the identity moved. */
export type OpenOutcome = 'opened' | 'gone' | 'failed'

interface UseConversationsReturn {
  // State
  conversations: ConversationSummary[]
  currentConversation: Conversation | null
  currentConversationId: string | null
  isLoading: boolean
  isSyncing: boolean
  error: string | null

  // Actions
  fetchConversations: () => Promise<void>
  fetchConversation: (id: string) => Promise<Conversation | null>
  createConversation: (personaId: string | null) => Promise<string>
  updateConversation: (id: string, updates: { title?: string; personaId?: string | null }) => Promise<void>
  deleteConversation: (id: string) => Promise<void>
  selectConversation: (id: string) => Promise<OpenOutcome>
  addMessage: (conversationId: string, message: MessageCreate) => Promise<string>
  syncMessageToBackend: (conversationId: string, message: MessageCreate) => Promise<void>
  syncAttachmentMessage: (
    conversationId: string,
    payload: { content: string; mediaRef: string; filename: string; mimeType: string }
  ) => Promise<SavedAttachmentMessage>
}

/** The identity a request is SENT as (SA-922): its answer is that person's. */
const signedInId = () => useAuthStore.getState().user?.id ?? null

/** The identity whose conversation list this page load has requested (SA-972).
 * Module state, shared by every mounted instance: one request per identity. */
let listRequestedFor: string | null = null

/** Ids the server answered 404 for, or deleted from this tab, as the signed-in
 * identity (SA-974). None is requested again. Written only for the identity a
 * request was sent as, and cleared at every identity change. */
const goneIds = new Set<string>()

/** Called at every identity change (services/identityChange). The store was
 * emptied, so the next mount, or a mounted instance, requests the list again,
 * and the previous person's gone ids are forgotten (SA-974). Keying the list
 * request on the user id alone would miss a logout then the same person's login. */
export function resetConversationListRequest(): void {
  listRequestedFor = null
  goneIds.clear()
}

export function useConversations(): UseConversationsReturn {
  const store = useChatStore()
  // The identity this instance's list request is in flight for; undefined =
  // none. A request for a previous identity never holds up the new one's (SA-972).
  const fetchingFor = useRef<string | null | undefined>(undefined)
  const userId = useAuthStore((s) => s.user?.id ?? null)

  const currentConversation = store.currentConversationId
    ? store.conversations[store.currentConversationId] || null
    : null

  const fetchConversations = useCallback(async () => {
    const sentFor = signedInId()
    // Prevent concurrent fetches for the same identity
    if (fetchingFor.current === sentFor) return
    fetchingFor.current = sentFor
    // An answer for a previous identity writes nothing (SA-972).
    const stillSignedIn = () => signedInId() === sentFor

    store.setLoading(true)
    store.setError(null)
    try {
      const response = await apiClient.fetch(`/api/conversations`)
      if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.statusText}`)
      }
      const data = await response.json()
      if (!stillSignedIn()) return

      // A list sent before a 404 or a delete would put a gone row back (SA-974).
      const rows = data.filter((c: Record<string, unknown>) => !goneIds.has(c.id as string))

      // Transform API response to match store format
      const transformed: ConversationSummary[] = rows.map((c: Record<string, unknown>) => ({
        id: c.id,
        title: c.title,
        personaId: c.persona_id,
        personaName: c.persona_name,
        channel: (c.channel as string) || 'web',
        messageCount: c.message_count,
        createdAt: new Date(c.created_at as string).getTime(),
        updatedAt: new Date(c.updated_at as string).getTime(),
      }))

      store.setConversationList(transformed)
    } catch (err) {
      if (stillSignedIn()) store.setError(err instanceof Error ? err.message : 'Failed to fetch conversations')
    } finally {
      if (stillSignedIn()) store.setLoading(false)
      if (fetchingFor.current === sentFor) fetchingFor.current = undefined
    }
  }, [store])

  const fetchConversation = useCallback(async (id: string): Promise<Conversation | null> => {
    // An id the server answered 404 for is not requested again (SA-974).
    if (goneIds.has(id)) return null

    // Check if already loaded (use store.getState for fresh data)
    const currentState = useChatStore.getState()
    const existing = currentState.conversations[id]
    if (existing && existing.messages.length > 0) {
      return existing
    }

    const sentFor = signedInId()
    // An answer for a previous identity writes nothing (SA-972).
    const stillSignedIn = () => signedInId() === sentFor
    store.setSyncing(true)
    try {
      const response = await apiClient.fetch(`/api/conversations/${id}`)
      if (!response.ok) {
        if (response.status === 404) {
          // Gone: its row and the selection leave once, and this identity
          // never requests it again (SA-974).
          if (stillSignedIn()) {
            goneIds.add(id)
            store.deleteConversation(id)
          }
          return null
        }
        throw new Error(`Failed to fetch conversation: ${response.statusText}`)
      }
      const data = await response.json()
      if (!stillSignedIn()) return null

      // Transform API response
      const conversation: Conversation = {
        id: data.id,
        title: data.title,
        personaId: data.persona_id,
        messages: data.messages.map((m: Record<string, unknown>) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
          sources: m.sources,
          error: m.error,
          cut: (m.cut as ReplyCut | null) ?? undefined,
          mediaType: (m.media_type as 'image' | 'document' | null) ?? undefined,
          mediaUrl: (m.media_url as string | null) ?? undefined,
          attachment:
            m.media_filename || m.media_mime_type
              ? {
                  filename: (m.media_filename as string) || 'attachment',
                  mimeType: (m.media_mime_type as string) || '',
                  byteSize: (m.media_byte_size as number | null) ?? undefined,
                }
              : undefined,
        })),
        createdAt: new Date(data.created_at).getTime(),
        updatedAt: new Date(data.updated_at).getTime(),
      }

      store.setConversation(conversation)
      return conversation
    } catch (err) {
      if (stillSignedIn()) store.setError(err instanceof Error ? err.message : 'Failed to fetch conversation')
      return null
    } finally {
      if (stillSignedIn()) store.setSyncing(false)
    }
  }, [store])

  const createConversation = useCallback(async (personaId: string | null): Promise<string> => {
    store.setSyncing(true)
    try {
      const response = await apiClient.fetch(`/api/conversations`, {
        method: 'POST',
        body: JSON.stringify({
          title: 'New Conversation',
          persona_id: personaId,
        }),
      })

      if (!response.ok) {
        throw new Error(`Failed to create conversation: ${response.statusText}`)
      }

      const data = await response.json()

      // Add to local store
      const conversation: Conversation = {
        id: data.id,
        title: data.title,
        personaId: data.persona_id,
        messages: [],
        createdAt: new Date(data.created_at).getTime(),
        updatedAt: new Date(data.updated_at).getTime(),
      }

      store.setConversation(conversation)
      store.setCurrentConversation(data.id)

      // Add to conversationList directly (avoids race condition where
      // fetchConversations() overwrites a locally-applied auto-name title)
      const currentList = useChatStore.getState().conversationList
      const newSummary: ConversationSummary = {
        id: data.id,
        title: data.title,
        personaId: data.persona_id,
        personaName: null,
        channel: data.channel || 'web',
        messageCount: 0,
        createdAt: new Date(data.created_at).getTime(),
        updatedAt: new Date(data.updated_at).getTime(),
      }
      store.setConversationList([newSummary, ...currentList])

      return data.id
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to create conversation')
      throw err
    } finally {
      store.setSyncing(false)
    }
  }, [store])

  const updateConversation = useCallback(async (
    id: string,
    updates: { title?: string; personaId?: string | null }
  ): Promise<void> => {
    store.setSyncing(true)
    try {
      const body: Record<string, unknown> = {}
      if (updates.title !== undefined) body.title = updates.title
      if (updates.personaId !== undefined) body.persona_id = updates.personaId

      const response = await apiClient.fetch(`/api/conversations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        throw new Error(`Failed to update conversation: ${response.statusText}`)
      }

      if (updates.title) {
        store.updateConversationTitle(id, updates.title)
      }
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to update conversation')
      throw err
    } finally {
      store.setSyncing(false)
    }
  }, [store])

  const deleteConversation = useCallback(async (id: string): Promise<void> => {
    const sentFor = signedInId()
    store.setSyncing(true)
    try {
      const response = await apiClient.fetch(`/api/conversations/${id}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        throw new Error(`Failed to delete conversation: ${response.statusText}`)
      }

      store.deleteConversation(id)
      // Never fetched again into a 404 (SA-974).
      if (signedInId() === sentFor) goneIds.add(id)
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to delete conversation')
      throw err
    } finally {
      store.setSyncing(false)
    }
  }, [store])

  const selectConversation = useCallback(async (id: string): Promise<OpenOutcome> => {
    // Gone for this identity: nothing is requested and nothing is written.
    // Writing the selection would rebuild these callbacks and re-run the Chat
    // page's effect (SA-974).
    if (goneIds.has(id)) return 'gone'

    const sentFor = signedInId()
    store.setCurrentConversation(id)

    // Load the full conversation unless it is already here. An id the list
    // does not hold (past its first page, or not this person's) is fetched
    // at once (SA-975).
    const existing = useChatStore.getState().conversations[id]
    if (existing && existing.messages.length > 0) return 'opened'

    const conversation = await fetchConversation(id)
    if (conversation) return 'opened'
    // A 404: fetchConversation dropped the row and the selection.
    if (goneIds.has(id)) return 'gone'
    // Not opened: clear the selection, unless it moved on or the identity moved
    // meanwhile (SA-972).
    if (signedInId() === sentFor && useChatStore.getState().currentConversationId === id) {
      store.setCurrentConversation(null)
    }
    return 'failed'
  }, [store, fetchConversation])

  const addMessage = useCallback(async (
    _conversationId: string,
    _message: MessageCreate
  ): Promise<string> => {
    // This is for optimistic local updates - actual sync happens via syncMessageToBackend
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    return id
  }, [])

  const syncMessageToBackend = useCallback(async (
    conversationId: string,
    message: MessageCreate
  ): Promise<void> => {
    try {
      const response = await apiClient.fetch(`/api/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify({
          role: message.role,
          content: message.content,
          sources: message.sources,
          error: message.error,
          cut: message.cut,
        }),
      })

      if (!response.ok) {
        console.error('Failed to sync message to backend:', response.statusText)
      }
    } catch (err) {
      console.error('Failed to sync message to backend:', err)
    }
  }, [])

  const syncAttachmentMessage = useCallback(
    async (
      conversationId: string,
      payload: { content: string; mediaRef: string; filename: string; mimeType: string }
    ): Promise<SavedAttachmentMessage> => {
      const response = await apiClient.fetch(
        `/api/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          body: JSON.stringify({
            role: 'user',
            content: payload.content,
            media_ref: payload.mediaRef,
            media_filename: payload.filename,
            media_mime_type: payload.mimeType,
          }),
        }
      )
      if (!response.ok) {
        throw new Error(`Failed to save attachment message (${response.status})`)
      }
      return response.json()
    },
    []
  )

  // Request the list once per signed-in identity across all instances (SA-972).
  // `userId`: a mounted instance requests the new identity's list at once; no
  // one signed in requests nothing.
  useEffect(() => {
    if (userId !== null && listRequestedFor !== userId) {
      listRequestedFor = userId
      fetchConversations()
    }
  }, [userId, fetchConversations])

  return {
    conversations: store.conversationList,
    currentConversation,
    currentConversationId: store.currentConversationId,
    isLoading: store.isLoading,
    isSyncing: store.isSyncing,
    error: store.error,
    fetchConversations,
    fetchConversation,
    createConversation,
    updateConversation,
    deleteConversation,
    selectConversation,
    addMessage,
    syncMessageToBackend,
    syncAttachmentMessage,
  }
}
